import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import * as XLSX from "xlsx";
import db from "./Firebase";

const parseDate = (val) => {
    if (!val) return null;
    if (typeof val?.toDate === "function") return val.toDate();
    if (val instanceof Date) return val;
    if (typeof val?.seconds === "number") return new Date(val.seconds * 1000);
    const d = new Date(val);
    return isNaN(d) ? null : d;
};

const fmtDate = (val) => {
    const d = parseDate(val);
    if (!d) return "-";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const cv = (val, fb = "-") => (val === null || val === undefined || val === "" ? fb : String(val));
const compactPhone = (val) => String(val || "").replace(/\D/g, "").slice(-10);

export default function AbcUsersData() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [cityFilter, setCityFilter] = useState("all");
    const [onboardFilter, setOnboardFilter] = useState("all");

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                const snap = await getDocs(query(collection(db, "funder_users_list"), where("funder", "==", "ABC")));
                const data = snap.docs.map(d => {
                    const doc = d.data();
                    return {
                        id: d.id,
                        phone: compactPhone(doc.phone_no),
                        name: cv(doc.name),
                        username: cv(doc.username),
                        city: cv(doc.city),
                        state: cv(doc.state),
                        funder: cv(doc.funder),
                        phase: cv(doc.phase),
                        type: cv(doc.type),
                        age_range: cv(doc.age_range),
                        start_date: doc.start_date || null,
                        end_date: doc.end_date || null,
                        isOnboarded: false,
                    };
                });

                // Batch-check personal_details by phone_no (max 10 per query)
                const phones = [...new Set(data.map(r => r.phone).filter(Boolean))];
                const onboardedSet = new Set();
                for (let i = 0; i < phones.length; i += 10) {
                    const chunk = phones.slice(i, i + 10);
                    const pdSnap = await getDocs(query(collection(db, "personal_details"), where("phone_no", "in", chunk)));
                    pdSnap.forEach(d => {
                        const ph = compactPhone(d.data().phone_no);
                        if (ph) onboardedSet.add(ph);
                    });
                }
                data.forEach(r => { r.isOnboarded = onboardedSet.has(r.phone); });

                data.sort((a, b) => (parseDate(b.end_date)?.getTime() || 0) - (parseDate(a.end_date)?.getTime() || 0));
                setRows(data);
            } catch (err) {
                console.error("AbcUsersData fetch error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, []);

    const cities = useMemo(() => {
        const set = new Set(rows.map(r => r.city).filter(c => c !== "-"));
        return ["all", ...Array.from(set).sort()];
    }, [rows]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows.filter(r => {
            const matchSearch = !q ||
                r.phone.includes(q) ||
                r.name.toLowerCase().includes(q) ||
                r.username.toLowerCase().includes(q) ||
                r.city.toLowerCase().includes(q) ||
                r.state.toLowerCase().includes(q) ||
                r.funder.toLowerCase().includes(q);
            const matchCity = cityFilter === "all" || r.city === cityFilter;
            const matchOnboard = onboardFilter === "all" || (onboardFilter === "yes" ? r.isOnboarded : !r.isOnboarded);
            return matchSearch && matchCity && matchOnboard;
        });
    }, [rows, search, cityFilter, onboardFilter]);

    const handleExport = () => {
        const sheetData = filtered.map((r, i) => ({
            "#": i + 1,
            "Name": r.name,
            "Phone No": r.phone,
            "Username": r.username,
            "City": r.city,
            "State": r.state,
            "Funder": r.funder,
            // "Phase": r.phase,
            // "Type": r.type,
            "Age Range": r.age_range,
            "Onboarded": r.isOnboarded ? "Onboarded" : "Not Onboarded",
            // "Start Date": fmtDate(r.start_date),
            // "End Date": fmtDate(r.end_date),
        }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheetData), "ABC Users");
        XLSX.writeFile(wb, "abc_users_data.xlsx");
    };

    const today = new Date();

    return (
        <div className="container-fluid py-4">

            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h3 className="mb-1">ABC Users Data</h3>
                    <div className="text-muted" style={{ fontSize: "13px" }}>
                        funder_users_list · {rows.length} total records
                    </div>
                </div>
                <Link to="/" className="btn btn-outline-secondary btn-sm">Back</Link>
            </div>

            {/* SEARCH + FILTER + EXPORT */}
            <div className="card mb-3">
                <div className="card-body py-3 px-4">
                    <div className="d-flex gap-3 align-items-center flex-wrap">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search by name, phone, username, city, state…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{ maxWidth: "320px" }}
                        />
                        <select
                            className="form-select"
                            value={cityFilter}
                            onChange={e => setCityFilter(e.target.value)}
                            style={{ maxWidth: "200px" }}
                        >
                            {cities.map(c => (
                                <option key={c} value={c}>{c === "all" ? "All Cities" : c}</option>
                            ))}
                        </select>
                        <select
                            className="form-select"
                            value={onboardFilter}
                            onChange={e => setOnboardFilter(e.target.value)}
                            style={{ maxWidth: "180px" }}
                        >
                            <option value="all">All</option>
                            <option value="yes">Onboarded</option>
                            <option value="no">Not Onboarded</option>
                        </select>
                        {(search || cityFilter !== "all" || onboardFilter !== "all") && (
                            <span className="text-muted" style={{ fontSize: "13px" }}>
                                {filtered.length} result{filtered.length !== 1 ? "s" : ""}
                            </span>
                        )}
                        <button
                            className="btn btn-success ms-auto"
                            onClick={handleExport}
                            disabled={loading || filtered.length === 0}
                            style={{ minWidth: "140px", fontWeight: 700 }}
                        >
                            Export Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* TABLE */}
            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" />
                    <div className="mt-2 text-muted" style={{ fontSize: "14px" }}>Loading…</div>
                </div>
            ) : (
                <div className="card">
                    <div className="table-responsive">
                        <table className="table table-bordered table-hover align-middle mb-0" style={{ fontSize: "13px" }}>
                            <thead className="table-dark">
                                <tr>
                                    <th>#</th>
                                    <th>Name</th>
                                    <th>Phone No</th>
                                    <th>Username</th>
                                    <th>City</th>
                                    <th>State</th>
                                    <th>Funder</th>
                                    {/* <th className="text-center">Phase</th> */}
                                    {/* <th>Type</th> */}
                                    <th>Age Range</th>
                                    <th className="text-center">Onboarded</th>
                                    {/* <th>Start Date</th>
                                    <th>End Date</th> */}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan="12" className="text-center py-4 text-muted">
                                            {search || cityFilter !== "all" ? "No results found" : "No data found"}
                                        </td>
                                    </tr>
                                ) : filtered.map((row, i) => {
                                    const endDate = parseDate(row.end_date);
                                    const isActive = endDate && endDate > today;
                                    return (
                                        <tr key={row.id}>
                                            <td>{i + 1}</td>
                                            <td style={{ fontWeight: 600 }}>{row.name}</td>
                                            <td>{row.phone}</td>
                                            <td>{row.username}</td>
                                            <td>{row.city}</td>
                                            <td>{row.state}</td>
                                            <td>
                                                <span className="badge bg-info text-dark" style={{ fontSize: "11px", padding: "3px 7px" }}>
                                                    {row.funder}
                                                </span>
                                            </td>
                                            {/* <td className="text-center">{row.phase}</td> */}
                                            {/* <td>{row.type}</td> */}
                                            <td>{row.age_range}</td>
                                            <td className="text-center">
                                                <span className={`badge ${row.isOnboarded ? "bg-success" : "bg-secondary"}`}
                                                    style={{ fontSize: "12px", padding: "5px 10px" }}>
                                                    {row.isOnboarded ? "Onboarded" : "Not Onboarded"}
                                                </span>
                                            </td>
                                            {/* <td style={{ whiteSpace: "nowrap" }}>{fmtDate(row.start_date)}</td>
                                            <td style={{ whiteSpace: "nowrap" }}>
                                                <span className={`badge ${isActive ? "bg-success" : "bg-danger"}`}
                                                    style={{ fontSize: "12px", padding: "5px 10px" }}>
                                                    {fmtDate(row.end_date)}
                                                </span>
                                            </td> */}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
